/**
 * RectBuilder - Rect Entity 생성을 위한 Builder
 */
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d, CanvasEngineExportEntityType } from '../../../types'
import { SHAPES } from '../../../const'
import type { RectInitConfig } from './rectEntity'

/**
 * RectBuilder 클래스
 */
export class RectBuilder {
  private canvasEngine: ICanvasEngineEntityController
  private config: Partial<RectInitConfig> = {}

  constructor(canvasEngine: ICanvasEngineEntityController) {
    this.canvasEngine = canvasEngine
    this.reset()
  }

  reset(): RectBuilder {
    this.config = { interactionMode: true }
    return this
  }

  setId(id: string): RectBuilder {
    this.config.id = id
    return this
  }

  setCreatePoint(point: Vector2d): RectBuilder {
    this.config.createPoint = point
    return this
  }

  setBalloonPoint(point: Vector2d): RectBuilder {
    this.config.balloonPoint = point
    return this
  }

  setColor(color: string): RectBuilder {
    if (!this.config.config) {
      this.config.config = { color }
    } else {
      this.config.config.color = color
    }
    return this
  }

  setSize(width: number, height: number): RectBuilder {
    if (!this.config.config) {
      this.config.config = { color: '#000000', width, height }
    } else {
      this.config.config.width = width
      this.config.config.height = height
    }
    return this
  }

  setRotation(rotation: number): RectBuilder {
    this.config.rotation = rotation
    return this
  }

  setLayerId(layerId: string): RectBuilder {
    this.config.layerId = layerId
    return this
  }

  setIsFill(isFill: boolean): RectBuilder {
    this.config.isFill = isFill
    return this
  }

  setShapeType(shapeType: 'line' | 'dash' | 'cloud'): RectBuilder {
    this.config.shapeType = shapeType
    return this
  }

  setBalloonFontColor(color: string): RectBuilder {
    this.config.balloonFontColor = color
    return this
  }

  setBalloonFillColor(color: string): RectBuilder {
    this.config.balloonFillColor = color
    return this
  }

  setBalloonScale(scale: number): RectBuilder {
    this.config.balloonScale = scale
    return this
  }

  setConnectedLineThicknessScale(scale: number): RectBuilder {
    this.config.connectedLineThicknessScale = scale
    return this
  }

  setBalloonFontScale(scale: number): RectBuilder {
    this.config.balloonFontScale = scale
    return this
  }

  setPointShapeScale(scale: number): RectBuilder {
    this.config.pointShapeScale = scale
    return this
  }

  setAreaBorderThicknessScale(scale: number): RectBuilder {
    this.config.areaBorderThicknessScale = scale
    return this
  }

  setInteractionMode(mode: boolean): RectBuilder {
    this.config.interactionMode = mode
    return this
  }

  fromExportData(data: CanvasEngineExportEntityType): RectBuilder {
    if (data.type !== SHAPES.RECT) {
      throw new Error('Invalid entity type for RectBuilder')
    }

    this.setId(data.shapeId)
      .setCreatePoint(data.point)
      .setBalloonPoint(data.balloonPoint)
      .setColor(data.color)
      .setLayerId(data.layerId)

    if (data.width !== undefined && data.height !== undefined) {
      this.setSize(data.width, data.height)
    }
    if (data.rotate !== undefined) this.setRotation(data.rotate)
    if (data.isFill !== undefined) this.setIsFill(data.isFill)
    if (data.rectType) this.setShapeType(data.rectType)
    if (data.balloonFontColor) this.setBalloonFontColor(data.balloonFontColor)
    if (data.balloonFillColor) this.setBalloonFillColor(data.balloonFillColor)
    if (data.balloonScale !== undefined && data.balloonScale !== null)
      this.setBalloonScale(data.balloonScale)
    if (data.connectedLineThicknessScale)
      this.setConnectedLineThicknessScale(data.connectedLineThicknessScale)
    if (data.balloonFontScale !== undefined && data.balloonFontScale !== null)
      this.setBalloonFontScale(data.balloonFontScale)
    if (data.pointShapeScale) this.setPointShapeScale(data.pointShapeScale)
    if (data.areaBorderThicknessScale)
      this.setAreaBorderThicknessScale(data.areaBorderThicknessScale)

    return this
  }

  build(): RectInitConfig {
    if (!this.config.config) {
      throw new Error('config is required')
    }

    return {
      id: this.config.id,
      createPoint: this.config.createPoint ?? { x: 0, y: 0 },
      balloonPoint: this.config.balloonPoint,
      config: this.config.config,
      rotation: this.config.rotation,
      layerId: this.config.layerId,
      isFill: this.config.isFill,
      shapeType: this.config.shapeType,
      balloonFontColor: this.config.balloonFontColor,
      balloonFillColor: this.config.balloonFillColor,
      balloonScale: this.config.balloonScale,
      connectedLineThicknessScale: this.config.connectedLineThicknessScale,
      balloonFontScale: this.config.balloonFontScale,
      pointShapeScale: this.config.pointShapeScale,
      areaBorderThicknessScale: this.config.areaBorderThicknessScale,
      interactionMode: this.config.interactionMode,
    }
  }

  getCanvasEngine(): ICanvasEngineEntityController {
    return this.canvasEngine
  }
}
